/**
 * Admin — Template Review.
 *
 * The queue every incoming template passes through before it can be public:
 * catalog imports, library syncs, AI generations and manual uploads all land
 * here as Pending Review. Nothing on this screen publishes by itself —
 * Approve records a verdict, and Publish is a separate, confirmed step.
 *
 * Built for volume. Filters, sort, page and the open preview all live in the
 * URL, so a reviewer can refresh, share or bookmark exactly where they are;
 * selection plus the bulk bar handles a page at a time; and the preview is
 * keyboard-driven for going through hundreds in a sitting.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2, RefreshCw, Search, ShieldCheck, Check, ThumbsDown, Trash2, Send, Globe, X, LayoutGrid,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import ReviewCard from './templateReview/ReviewCard';
import ReviewPreview from './templateReview/ReviewPreview';
import { NoteDialog, ConfirmDialog, MetadataDialog } from './templateReview/ReviewDialogs';
import {
  listTemplates, countTemplates, reviewSummary, workflow, bulkAction,
  removeTemplate, duplicateTemplate, updateMetadata,
} from './templateReview/reviewApi';
import {
  STATUS_TABS, SOURCES, FORMATS, SORTS, PAGE_SIZES, DENSITIES,
} from './templateReview/reviewConstants';

const DEFAULTS = { status: 'pending', source: 'all', category: '', style: '', format: '', q: '', sort: 'newest', page: '1', size: '60', density: 'comfortable' };

const selectClass = 'h-9 px-2.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-300';

const AdminAITemplates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  // ── URL-backed view state ────────────────────────────────────────────────
  const view = useMemo(() => {
    const read = (key) => params.get(key) ?? DEFAULTS[key];
    return {
      status: read('status'), source: read('source'), category: read('category'), style: read('style'),
      format: read('format'), q: read('q'), sort: read('sort'),
      page: Math.max(1, parseInt(read('page'), 10) || 1),
      size: PAGE_SIZES.includes(parseInt(read('size'), 10)) ? parseInt(read('size'), 10) : 60,
      density: DENSITIES[read('density')] ? read('density') : 'comfortable',
      preview: params.get('preview'),
    };
  }, [params]);

  /** Patch the URL; values equal to the default are dropped to keep it short. */
  const patch = useCallback((changes, { replace = false } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || String(value) === DEFAULTS[key]) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  }, [setParams]);

  const filters = useMemo(() => ({
    status: view.status, source: view.source, category: view.category, style: view.style, format: view.format, search: view.q,
  }), [view.status, view.source, view.category, view.style, view.format, view.q]);

  // ── data ─────────────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ by_status: {}, categories: [], styles: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [note, setNote] = useState(null);          // { kind: 'reject' | 'revise', targets }
  const [confirm, setConfirm] = useState(null);    // { kind: 'delete' | 'publish', targets }
  const [metaTarget, setMetaTarget] = useState(null);
  const [searchText, setSearchText] = useState(view.q);
  const requestSeq = useRef(0);

  const loadSummary = useCallback(() => {
    reviewSummary(view.source).then(setSummary).catch(() => {});
  }, [view.source]);

  const load = useCallback(async () => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    try {
      const [rows, count] = await Promise.all([
        listTemplates(filters, { page: view.page, limit: view.size, sort: view.sort }),
        countTemplates(filters),
      ]);
      if (seq !== requestSeq.current) return;       // a newer request superseded this one
      setTemplates(rows);
      setTotal(count);
    } catch (err) {
      if (seq === requestSeq.current) toast({ title: 'Failed to load templates', description: err.message, variant: 'destructive' });
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [filters, view.page, view.size, view.sort, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setSelected(new Set()); }, [filters, view.page, view.size, view.sort]);

  // Search is typed locally and committed to the URL after a pause.
  useEffect(() => { setSearchText(view.q); }, [view.q]);
  useEffect(() => {
    if (searchText === view.q) return undefined;
    const timer = setTimeout(() => patch({ q: searchText, page: 1 }, { replace: true }), 320);
    return () => clearTimeout(timer);
  }, [searchText, view.q, patch]);

  const setFilter = (changes) => patch({ ...changes, page: 1, preview: null });

  // ── actions ──────────────────────────────────────────────────────────────
  /** After a status change the template leaves this tab: drop it locally and refresh the counts. */
  const settle = useCallback((ids) => {
    const gone = new Set(ids);
    setTemplates((prev) => prev.filter((t) => !gone.has(t.id)));
    setTotal((n) => Math.max(0, n - ids.length));
    setSelected((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
    loadSummary();
  }, [loadSummary]);

  const act = useCallback(async (template, action, body, done) => {
    setBusyId(template.id);
    try {
      await workflow(template.id, action, body);
      toast({ title: done, description: template.name });
      settle([template.id]);
      return true;
    } catch (err) {
      toast({ title: 'Action failed', description: err.message, variant: 'destructive' });
      return false;
    } finally {
      setBusyId(null);
    }
  }, [settle, toast]);

  const approve = useCallback((t) => act(t, 'approve', {}, 'Approved — not yet public'), [act]);
  const unpublish = useCallback((t) => act(t, 'unpublish', {}, 'Unpublished'), [act]);
  const resubmit = useCallback((t) => act(t, 'submit', {}, 'Returned to pending review'), [act]);
  const askReject = useCallback((t) => setNote({ kind: 'reject', targets: [t] }), []);
  const askRevise = useCallback((t) => setNote({ kind: 'revise', targets: [t] }), []);
  const askPublish = useCallback((t) => setConfirm({ kind: 'publish', targets: [t] }), []);
  const askDelete = useCallback((t) => setConfirm({ kind: 'delete', targets: [t] }), []);

  const edit = useCallback((t) => {
    // Same flow as Templates → Edit Canvas: EditorPage detects the admin path
    // and saves back to the templates collection.
    navigate(`/admin/templates/${t.id}/edit`, { state: { template: t } });
  }, [navigate]);

  const duplicate = useCallback(async (t) => {
    setBusyId(t.id);
    try {
      const copy = await duplicateTemplate(t.id);
      toast({ title: 'Duplicated as a draft', description: copy.name });
      loadSummary();
    } catch (err) {
      toast({ title: 'Duplicate failed', description: err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  }, [loadSummary, toast]);

  const runBulk = async (ids, action, extra, done) => {
    setBulkBusy(true);
    try {
      const res = await bulkAction(ids, action, extra);
      toast({ title: `${res.modified} ${done}` });
      settle(ids);
      return true;
    } catch (err) {
      toast({ title: 'Bulk action failed', description: err.message, variant: 'destructive' });
      return false;
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmNote = async (text) => {
    const { kind, targets } = note;
    const ids = targets.map((t) => t.id);
    const ok = kind === 'reject'
      ? await runBulk(ids, 'reject', { rejection_reason: text }, 'rejected')
      : await runBulk(ids, 'request_revision', { revision_notes: text }, 'sent back for revisions');
    if (ok) setNote(null);
  };

  const confirmAction = async () => {
    const { kind, targets } = confirm;
    const ids = targets.map((t) => t.id);
    let ok;
    if (kind === 'publish') ok = await runBulk(ids, 'publish', {}, 'published');
    else if (ids.length === 1) {
      setBulkBusy(true);
      try { await removeTemplate(ids[0]); toast({ title: 'Deleted', description: targets[0].name }); settle(ids); ok = true; }
      catch (err) { toast({ title: 'Delete failed', description: err.message, variant: 'destructive' }); ok = false; }
      finally { setBulkBusy(false); }
    } else ok = await runBulk(ids, 'delete', {}, 'deleted');
    if (ok) setConfirm(null);
  };

  const saveMetadata = async (fields) => {
    setBulkBusy(true);
    try {
      const updated = await updateMetadata(metaTarget.id, fields);
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      toast({ title: 'Details saved', description: updated.name });
      setMetaTarget(null);
      loadSummary();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  // ── selection ────────────────────────────────────────────────────────────
  const toggle = useCallback((id) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);
  const allOnPage = templates.length > 0 && templates.every((t) => selected.has(t.id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(templates.map((t) => t.id)));
  const chosen = templates.filter((t) => selected.has(t.id));

  // ── preview ──────────────────────────────────────────────────────────────
  const previewIndex = view.preview ? templates.findIndex((t) => t.id === view.preview) : -1;
  const previewed = previewIndex >= 0 ? templates[previewIndex] : null;
  const lastIndex = useRef(-1);
  useEffect(() => { if (previewIndex >= 0) lastIndex.current = previewIndex; }, [previewIndex]);
  // The previewed template was approved/rejected and left the list: move to
  // whatever now sits in its place, so review continues without a click.
  useEffect(() => {
    if (!view.preview || loading || previewIndex >= 0) return;
    const next = lastIndex.current >= 0 ? templates[Math.min(lastIndex.current, templates.length - 1)] : null;
    patch({ preview: next ? next.id : null }, { replace: true });
  }, [view.preview, previewIndex, templates, loading, patch]);

  const openPreview = useCallback((t) => patch({ preview: t.id }), [patch]);
  const closePreview = useCallback(() => patch({ preview: null }), [patch]);
  const step = useCallback((delta) => {
    const next = templates[previewIndex + delta];
    if (next) patch({ preview: next.id }, { replace: true });
  }, [templates, previewIndex, patch]);
  const prev = useCallback(() => step(-1), [step]);
  const next = useCallback(() => step(1), [step]);

  const pages = Math.max(1, Math.ceil(total / view.size));
  const density = DENSITIES[view.density];

  return (
    <AdminLayout>
      <div className="p-6 max-w-[1800px] mx-auto">
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Template Review</h1>
              <p className="text-sm text-gray-500">Nothing is public until it is approved and then published.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => { load(); loadSummary(); }}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* status tabs */}
        <div className="flex flex-wrap gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="Review status">
          {STATUS_TABS.map((tab) => {
            const count = summary.by_status?.[tab.key] ?? 0;
            const active = view.status === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter({ status: tab.key })}
                className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              >
                {tab.label}
                <span className={`ml-1.5 text-[11px] tabular-nums px-1.5 py-0.5 rounded-full ${active ? 'bg-violet-100 text-violet-700' : 'bg-gray-200 text-gray-600'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 pointer-events-none" />
            <input
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search name, tag or subcategory"
              aria-label="Search templates"
              className="h-9 w-64 pl-8 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <select aria-label="Source" className={selectClass} value={view.source} onChange={(e) => setFilter({ source: e.target.value })}>
            {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select aria-label="Category" className={selectClass} value={view.category} onChange={(e) => setFilter({ category: e.target.value })}>
            <option value="">All categories</option>
            {summary.categories?.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
          </select>
          <select aria-label="Style" className={selectClass} value={view.style} onChange={(e) => setFilter({ style: e.target.value })}>
            <option value="">All styles</option>
            {summary.styles?.map((s) => <option key={s.style} value={s.style}>{s.style} ({s.count})</option>)}
          </select>
          <select aria-label="Format" className={selectClass} value={view.format} onChange={(e) => setFilter({ format: e.target.value })}>
            {FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <select aria-label="Sort" className={selectClass} value={view.sort} onChange={(e) => patch({ sort: e.target.value, page: 1 })}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-gray-400" />
            <select aria-label="Card size" className={selectClass} value={view.density} onChange={(e) => patch({ density: e.target.value })}>
              {Object.entries(DENSITIES).map(([key, d]) => <option key={key} value={key}>{d.label}</option>)}
            </select>
            <select aria-label="Templates per page" className={selectClass} value={view.size} onChange={(e) => patch({ size: e.target.value, page: 1 })}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
            </select>
          </div>
        </div>

        {/* selection / bulk bar */}
        <div className="sticky top-0 z-20 -mx-2 px-2 py-2 mb-3 bg-gray-50/95 backdrop-blur flex flex-wrap items-center gap-2 border-b border-gray-200">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={allOnPage} onChange={toggleAll} disabled={templates.length === 0} className="w-4 h-4 accent-violet-600" aria-label="Select all on this page" />
            {selected.size > 0 ? `${selected.size} selected` : 'Select page'}
          </label>
          <span className="text-sm text-gray-400 tabular-nums" data-testid="review-total">{total} template{total === 1 ? '' : 's'}</span>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 ml-2" data-testid="bulk-bar">
              {view.status !== 'approved' && view.status !== 'published' && (
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={bulkBusy}
                  onClick={() => runBulk([...selected], 'approve', {}, 'approved — not yet public')}>
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
              )}
              {view.status === 'approved' && (
                <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={bulkBusy}
                  onClick={() => setConfirm({ kind: 'publish', targets: chosen })}>
                  <Globe className="w-4 h-4 mr-1" /> Publish
                </Button>
              )}
              {view.status !== 'rejected' && view.status !== 'published' && (
                <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" disabled={bulkBusy}
                  onClick={() => setNote({ kind: 'reject', targets: chosen })}>
                  <ThumbsDown className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
              {(view.status === 'pending' || view.status === 'approved') && (
                <Button size="sm" variant="outline" className="h-8" disabled={bulkBusy} onClick={() => setNote({ kind: 'revise', targets: chosen })}>
                  <Send className="w-4 h-4 mr-1" /> Request revisions
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" disabled={bulkBusy}
                onClick={() => setConfirm({ kind: 'delete', targets: chosen })}>
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <button type="button" onClick={() => setSelected(new Set())} className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 ml-1">
                <X className="w-3.5 h-3.5 mr-0.5" /> Clear
              </button>
              {bulkBusy && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
            </div>
          )}
        </div>

        {/* grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2">
            <ShieldCheck className="w-10 h-10 opacity-30" />
            <p className="text-sm">No templates match these filters.</p>
          </div>
        ) : (
          <div className={`grid gap-4 ${density.grid}`} data-testid="review-grid">
            {templates.map((t) => (
              <ReviewCard
                key={t.id}
                template={t}
                compact={view.density === 'compact'}
                selected={selected.has(t.id)}
                busy={busyId === t.id}
                onToggle={toggle}
                onPreview={openPreview}
                onEdit={edit}
                onApprove={approve}
                onPublish={askPublish}
                onReject={askReject}
                onDelete={askDelete}
                onDuplicate={duplicate}
              />
            ))}
          </div>
        )}

        {/* pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button variant="outline" size="sm" disabled={view.page <= 1} onClick={() => patch({ page: view.page - 1 })}>Previous</Button>
            <span className="text-sm text-gray-600 tabular-nums">Page {view.page} of {pages}</span>
            <Button variant="outline" size="sm" disabled={view.page >= pages} onClick={() => patch({ page: view.page + 1 })}>Next</Button>
          </div>
        )}
      </div>

      {previewed && (
        <ReviewPreview
          template={previewed}
          index={previewIndex}
          total={templates.length}
          busy={busyId === previewed.id || bulkBusy}
          onClose={closePreview}
          onPrev={prev}
          onNext={next}
          onApprove={approve}
          onReject={askReject}
          onRevise={askRevise}
          onPublish={askPublish}
          onUnpublish={unpublish}
          onSubmit={resubmit}
          onEdit={edit}
          onEditMeta={setMetaTarget}
          onDuplicate={duplicate}
          onDelete={askDelete}
          onOpenSimilar={(other) => patch({ status: other.status, preview: other.id, page: 1, q: other.name })}
        />
      )}

      <NoteDialog kind={note?.kind} targets={note?.targets} busy={bulkBusy} onCancel={() => setNote(null)} onConfirm={confirmNote} />
      <ConfirmDialog kind={confirm?.kind} targets={confirm?.targets} busy={bulkBusy} onCancel={() => setConfirm(null)} onConfirm={confirmAction} />
      <MetadataDialog template={metaTarget} categories={summary.categories || []} styles={summary.styles || []} busy={bulkBusy} onCancel={() => setMetaTarget(null)} onSave={saveMetadata} />
    </AdminLayout>
  );
};

export default AdminAITemplates;
